<template>
  <bulk-update-wrapper
    :saving="saving"
    title="Remove Jobs From Batches"
    :button-label="buttonLabel"
    :has-changes="true"
    @save="save"
    @dismiss="dismiss"
  >
    <vs-text>
      {{ description }}
    </vs-text>

    <template #error-snackbar>
      <vs-snackbar :value="error" type="error" sticky @input="clearSnackbar">
        Failed to remove jobs from their batches
      </vs-snackbar>
    </template>
  </bulk-update-wrapper>
</template>

<script setup lang="ts">
import BulkUpdateWrapper from '../common/BulkUpdateWrapper.vue'
import { ref, computed } from 'vue'
import { JobJson } from '@/models/types/job-json'
import { BulkUpdateResponse } from '@/models/types/bulk-update-response'
import { useRemoveJobsFromTheirInvoicesMutation } from '@/api/invoice'

const props = defineProps<{
  selectedJobs: JobJson[]
}>()
const emits = defineEmits<{
  (e: 'dismiss')
  (e: 'updated', { message, updatedJobs }: BulkUpdateResponse)
}>()

const error = ref(false)
const clearSnackbar = () => (error.value = false)
const dismiss = () => emits('dismiss')
const buttonLabel = computed(() => {
  const jobCount = props.selectedJobs.length
  return jobCount >= 2
    ? `Remove ${jobCount} jobs from their batches`
    : 'Remove this job from its batch'
})
const description = computed(() => {
  const jobCount = props.selectedJobs.length
  if (jobCount >= 2) {
    return `Are you sure you want to remove these ${jobCount} jobs from their batches?`
  }
  return 'Are you sure you want to remove this job from its batch?'
})
const { mutateAsync: removeJobsFromBatch, isLoading: saving } =
  useRemoveJobsFromTheirInvoicesMutation()
const save = async () => {
  try {
    await removeJobsFromBatch({
      jobIds: props.selectedJobs.map((j) => j.id),
    })
    error.value = false
    const updatedJobs: JobJson[] = props.selectedJobs.map(({ ...job }) =>
      Object.assign(job, { invoice_id: null }),
    )
    emits('updated', {
      message: 'Jobs removed from their batches',
      updatedJobs,
    })
  } catch (e) {
    error.value = true
  }
}
</script>

<style scoped></style>
